import { defineStore, storeToRefs } from 'pinia';

import { useDidShow } from '@eaphone/hooks';
import { Network } from '@eaphone/network/next.ts';
import { sessionStore } from '@eaphone/storage';
import type {
  FamilyDetail,
  FamilyFlatted,
  FamilyGroups,
} from '@eaphone/types/resources';

function getFamilies() {
  return Network.custom.get('family/v1/family/group/');
}

function getFamily(familyId: string) {
  return Network.custom.get(`family/v1/family/${familyId}/`);
}

function createFamily(name: string) {
  return Network.custom.post('family/v1/family/', {
    searchParams: { name },
  });
}

function renameFamily({ familyId, name }: { familyId: string; name: string }) {
  return Network.custom.patch(`family/v1/family/${familyId}/name/`, {
    searchParams: { name },
  });
}

function removeFamily(familyId: string) {
  return Network.custom.delete(`family/v1/family/${familyId}/`);
}

function removeMeFromFamily(familyId: string) {
  return Network.custom.delete(`family/v1/family/${familyId}/member/exit/`);
}

function removeMemberFromFamily({
  familyId,
  memberId,
}: {
  familyId: string;
  memberId: string;
}) {
  return Network.custom.delete(
    `family/v1/family/${familyId}/member/${memberId}/`,
  );
}

function editRemark({
  familyId,
  memberId,
  remark,
}: {
  familyId: string;
  memberId: string;
  remark: string;
}) {
  return Network.custom.patch(
    `family/v1/family/${familyId}/member/${memberId}/remark/`,
    {
      searchParams: { remark },
    },
  );
}

function addMemberToFamilyByName({
  familyId,
  name,
}: {
  familyId: string;
  name: string;
}) {
  return Network.custom.post(`family/v1/family/${familyId}/member/virtual`, {
    json: { name },
  });
}

function moveDeviceToFamily({
  from,
  serial,
  to,
}: {
  from: string;
  serial: string;
  to: string;
}) {
  return Network.custom.patch(
    `family/v1/device/serial/${serial}/transfer/${from}/${to}`,
  );
}

function flatten({
  joins = [],
  creates = [],
}: FamilyGroups = {}): FamilyFlatted {
  return [...creates, ...joins];
}

type State = {
  data: FamilyGroups;
  temp: Record<string, FamilyDetail>;
  selected?: string;
};

export const useFamilyStore = defineStore('family', {
  state: (): State => ({
    data: {},
    temp: {},
    selected: sessionStore.get('family-selected'),
  }),
  getters: {
    all: ({ data }): FamilyFlatted => flatten(data),
    count(): number {
      return this.all.length;
    },
    current: ({ temp, selected }): FamilyDetail | undefined =>
      selected ? temp[selected] : undefined,
    devices(): FamilyDetail['devices'] {
      const { current: { devices = [] } = {} } = this;

      return devices;
    },
    members(): FamilyDetail['members'] {
      const { current: { members = [] } = {} } = this;

      return members;
    },
  },
  actions: {
    async select({ id }: { id: string }) {
      this.selected = id;
      sessionStore.set('family-selected', id);

      return this.getFamily(id);
    },
    async fetchData() {
      return getFamilies()
        .then((data) => {
          this.data = data;

          const list = flatten(data);

          const { selected } = this;

          if (selected && list.some(({ id }) => selected === id)) {
            this.getFamily(selected);
          } else if (list[0]) {
            this.select({ id: list[0].id });
          }

          return data;
        })
        .catch((error) => {
          this.data = {};
          throw error;
        });
    },
    async getFamily(id: string) {
      return getFamily(id).then((data) => {
        this.temp[data.id] = data;

        return data;
      });
    },
    async getDevice(familyId: string) {
      return this.getFamily(familyId);
    },
    async removeMeFromFamily(familyId: string) {
      return removeMeFromFamily(familyId).then(() => {
        this.fetchData();
      });
    },
    async createFamily(name: string) {
      return createFamily(name).then((data) => {
        this.fetchData();

        return data;
      });
    },
    async removeFamily(familyId: string) {
      return removeFamily(familyId).then(() => {
        this.fetchData();
      });
    },
    async renameFamily({ familyId, name }: { familyId: string; name: string }) {
      return renameFamily({ familyId, name }).then(() => {
        this.fetchData();
      });
    },
    async removeMemberFromFamily({
      familyId,
      memberId,
    }: {
      familyId: string;
      memberId: string;
    }) {
      return removeMemberFromFamily({ familyId, memberId }).then(() => {
        this.getFamily(familyId);
      });
    },
    async moveDeviceToFamily({
      from,
      serial,
      to,
    }: {
      from: string;
      serial: string;
      to: string;
    }) {
      return moveDeviceToFamily({ from, serial, to }).then(() => {
        this.fetchData();
      });
    },
    async addMemberToFamilyByName({
      familyId,
      name,
    }: {
      familyId: string;
      name: string;
    }) {
      return addMemberToFamilyByName({ familyId, name }).then(() => {
        this.getFamily(familyId);
      });
    },
    async editRemark({
      familyId,
      memberId,
      remark,
    }: {
      familyId: string;
      memberId: string;
      remark: string;
    }) {
      return editRemark({ familyId, memberId, remark }).then(() => {
        this.getFamily(familyId);
      });
    },
  },
});

export function useFamily() {
  const store = useFamilyStore();

  const io = storeToRefs(store);

  useDidShow(() => {
    if (store.count === 0) {
      store.fetchData();
    }
  });

  return io;
}
